import { Space } from 'antd';
import axios, { type AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import type {
  CleanKindAndReservationRequest,
  CleanReservationRuleResponse,
} from '../sharedTypes';
import CleanKindList from './CleanKindList';
import CleanKindManagementRules from './CleanKindManagementRules';
import DailyReservationLimit from './DailyReservationLimit';
import { getCleanKindRulesFormValues, getMonthLimitList } from './helper';
import styles from './index.module.scss';
import { CleanKindAndReservationResponse } from './type';

const Main = () => {
  const { mutate } = useSWRConfig();

  const [showType, setShowType] =
    useState<CleanKindAndReservationRequest['dataType']>('Limit');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());

  const reservationLimitListFetcherData = {
    year: selectedYear,
    month: selectedMonth,
    dataType: showType,
    cleanKindIdList: selectedOptions,
  };

  const stateSetters = {
    setShowType,
    setSelectedOptions,
    setSelectedYear,
    setSelectedMonth,
  };

  const getCleanKindRules = async (url: string) => {
    const res: AxiosResponse<CleanReservationRuleResponse> = await axios({
      method: 'GET',
      url,
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
    });
    const cleanKindRules = getCleanKindRulesFormValues(res.data);
    return cleanKindRules;
  };

  const getCleanKindAndReservationLimitList = async (
    url: string,
    data: {
      year: number;
      month: number;
      dataType: CleanKindAndReservationRequest['dataType'];
      cleanKindIdList: string[];
    }
  ) => {
    const res: AxiosResponse<CleanKindAndReservationResponse> = await axios({
      method: 'POST',
      url,
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
      data: {
        ...data,
        month: data.month + 1,
      },
    });
    const { kinds, limits } = res.data;
    const kindNames = kinds.map(({ cleanKindId, name }) => ({
      cleanKindId,
      name,
    }));
    const dailyReservationLimits = getMonthLimitList(limits, kindNames);
    return { kinds, dailyReservationLimits };
  };

  const { data: cleanKindRules } = useSWR(
    '/ezcon/api/fetchReservationRule',
    getCleanKindRules,
    {
      revalidateOnFocus: false,
    }
  );
  const { data: cleanKindAndReservationLimitList } = useSWR(
    '/ezcon/api/listCleanKindAndReservation',
    (url) =>
      getCleanKindAndReservationLimitList(url, reservationLimitListFetcherData),
    { revalidateOnFocus: false }
  );

  const handleReservationLimitFilterChange = (
    key?: string,
    value?:
      | string
      | number
      | CleanKindAndReservationRequest['dataType']
      | string[]
  ) => {
    if (key && value) {
      mutate('/ezcon/api/listCleanKindAndReservation', () =>
        getCleanKindAndReservationLimitList(
          '/ezcon/api/listCleanKindAndReservation',
          {
            ...reservationLimitListFetcherData,
            [key]: value,
          }
        )
      );
      return;
    }
    mutate('/ezcon/api/listCleanKindAndReservation', () =>
      getCleanKindAndReservationLimitList(
        '/ezcon/api/listCleanKindAndReservation',
        reservationLimitListFetcherData
      )
    );
  };

  const getLatestData = () => {
    mutate('/ezcon/api/fetchReservationRule');
    mutate('/ezcon/api/listCleanKindAndReservation', () =>
      getCleanKindAndReservationLimitList(
        '/ezcon/api/listCleanKindAndReservation',
        reservationLimitListFetcherData
      )
    );
  };

  return (
    <Space direction="vertical" className={styles.pageContainer} size={50}>
      <CleanKindManagementRules
        cleanKindRules={cleanKindRules}
        getLatestData={getLatestData}
      />
      <CleanKindList
        cleanKindRules={cleanKindRules}
        cleanKindAndReservationLimitList={cleanKindAndReservationLimitList}
        getLatestData={getLatestData}
      />
      <DailyReservationLimit
        showType={showType}
        stateSetters={stateSetters}
        selectedOptions={selectedOptions}
        cleanKindAndReservationLimitList={cleanKindAndReservationLimitList}
        handleFiltersChange={handleReservationLimitFilterChange}
      />
    </Space>
  );
};

export default Main;
